import React from "react";
import { formatDate, formatDateTime } from "../../../../utils/datetime";
import { DropDownList } from "../../../../components/ui";

/**
 * TicketLogRow - Default row component for TicketLogTable
 * 
 * A generic row renderer that uses column configuration to display values.
 * Can be used as-is for simple tables or as a reference for custom row components.
 * 
 * For module-specific rendering (like T&M with pricing, status dots, etc.),
 * create a custom RowComponent and pass it to TicketLogTable.
 * 
 * @example
 * // Using default TicketLogRow
 * <TicketLogTable
 *   data={tickets}
 *   columns={columns}
 *   RowComponent={TicketLogRow}
 *   rowProps={{
 *     onClone: (item) => handleClone(item),
 *     customActions: (item) => [
 *       <Button key="edit" onClick={() => handleEdit(item)}>Edit</Button>,
 *       <Button key="price" onClick={() => handlePrice(item)}>Price</Button>
 *     ]
 *   }}
 * />
 * 
 * // Custom row component for more control
 * const MyCustomRow = ({ item, columns, onRowClick }) => (
 *   <tr onClick={() => onRowClick(item)}>
 *     <td>{item.id}</td>
 *     <td><MyStatusIndicator status={item.status} /></td>
 *     ...
 *   </tr>
 * );
 * 
 * @param {Object} props
 * @param {Object} props.item - The ticket/item data object
 * @param {Array} props.columns - Column configuration array
 * @param {Function} props.onRowClick - Callback when row is clicked
 * @param {Function} [props.onClone] - Optional callback when clone is clicked: (item) => void
 * @param {Function} [props.onDelete] - Optional callback when delete is clicked: (item) => void
 * @param {Array<React.ReactElement>|Function} [props.customActions] - Optional custom action components to render before the kebab menu.
 *   Can be an array of React elements or a function that receives the item and returns React elements: (item) => React.ReactElement[]
 * @param {number|string} [props.highlightAddedItemId] - When set and equal to item.id, row shows a brief green flash (e.g. after clone)
 * @param {number|string} [props.highlightDeletedItemId] - When set and equal to item.id, row shows a brief red flash (before delete)
 * @param {Function} [props.onDeleteRequest] - When provided, Delete menu triggers this (opens confirm modal) instead of onDelete
 */
const TicketLogRow = ({ item, columns, onRowClick, onClone, onDelete, onDeleteRequest, customActions, highlightAddedItemId, highlightDeletedItemId }) => {
  // Get cell value from item based on column key (supports nested keys)
  const getCellValue = (column) => {
    // Use custom render function if provided
    if (column.render) {
      return column.render(item);
    }

    // Get value by key (supports nested like 'pricing.total_cost')
    let value = column.key.split('.').reduce((obj, key) => obj?.[key], item);

    // Apply formatter if provided (check for non-empty value)
    if (column.format && value != null && value !== "") {
      try {
        switch (column.format) {
          case "date":
            return formatDate(value, column.dateFormat || "mon DD, YYYY");
          case "datetime":
            return formatDateTime(value, column.dateFormat || "mon DD, YYYY");
          case "currency":
            return value
              ? `$${parseFloat(value).toLocaleString("en-US", {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })}`
              : column.emptyValue || "-";
          default:
            return value;
        }
      } catch (e) {
        console.warn(`Failed to format value "${value}" for column "${column.key}":`, e);
        return column.emptyValue || "-";
      }
    }

    return value ?? column.emptyValue ?? "-";
  };

  // Handle row click - but not when clicking on actions dropdown or custom actions
  const handleRowClick = (e) => {
    // Don't trigger row click if clicking on dropdown, custom actions, or their children
    if (e.target.closest('.dropdown-actions-container') || e.target.closest('.custom-actions-container')) {
      return;
    }
    onRowClick?.(item);
  };

  // Handle actions column rendering
  const renderActionsCell = (column) => {
    const listItems = {};
    const disabledItems = [];
    
    // Add Clone option if handler provided
    if (onClone) {
      listItems["Clone"] = () => onClone(item);
    }
    
    // Add Delete option: onDeleteRequest opens confirm modal; onDelete runs delete directly (legacy)
    if (onDeleteRequest) {
      listItems["Delete"] = () => onDeleteRequest(item);
    } else if (onDelete) {
      listItems["Delete"] = () => onDelete(item);
    } else {
      listItems["Delete"] = () => {};
      disabledItems.push("Delete");
    }

    // Get custom actions - can be array or function
    let customActionsElements = [];
    if (customActions) {
      if (typeof customActions === 'function') {
        customActionsElements = customActions(item);
      } else if (Array.isArray(customActions)) {
        customActionsElements = customActions;
      }
    }

    return (
      <div 
        className="flex gap-2 justify-end items-center"
        onClick={(e) => e.stopPropagation()}
        onMouseDown={(e) => e.stopPropagation()}
      >
        {/* Custom actions (e.g., Edit, Price buttons) */}
        {customActionsElements.length > 0 && (
          <div 
            className="custom-actions-container flex gap-2 items-center"
            onClick={(e) => e.stopPropagation()}
            onMouseDown={(e) => e.stopPropagation()}
          >
            {customActionsElements.map((action, index) => (
              <React.Fragment key={index}>
                {action}
              </React.Fragment>
            ))}
          </div>
        )}
        
        {/* Kebab menu dropdown */}
        <div 
          className="dropdown-actions-container"
          onClick={(e) => e.stopPropagation()}
          onMouseDown={(e) => e.stopPropagation()}
        >
          <DropDownList
            buttonIcon="dotsVertical"
            listItems={listItems}
            disabledItems={disabledItems}
            align="right"
            className="relative"
            buttonClassName="text-gray-600 hover:text-gray-900"
          />
        </div>
      </div>
    );
  };

  const isHighlighted = highlightAddedItemId != null && item.id === parseInt(highlightAddedItemId, 10);
  const isDeletedHighlight = highlightDeletedItemId != null && item.id === parseInt(highlightDeletedItemId, 10);

  return (
    <tr
      className={`hover:bg-gray-50 text-sm cursor-pointer ${isHighlighted ? "animate-clone-flash" : ""} ${isDeletedHighlight ? "animate-delete-flash" : ""}`}
      onClick={handleRowClick}
    >
      {columns.map((column) => {
        // Check if this is the actions column
        if (column.key === "actions") {
          return (
            <td
              key={column.key}
              className={`${column.cellClassName || "px-2 py-4 text-center"} ${
                column.hideOnMobile ? "hidden md:table-cell" : ""
              }`}
              onClick={(e) => e.stopPropagation()}
              onMouseDown={(e) => e.stopPropagation()}
            >
              {renderActionsCell(column)}
            </td>
          );
        }

        const style = {};
        if (column.width) style.width = column.width;
        if (column.maxWidth) style.maxWidth = column.maxWidth;
        if (column.minWidth) style.minWidth = column.minWidth;
        
        return (
          <td
            key={column.key}
            className={`${column.cellClassName || "px-6 py-4"} ${
              column.hideOnMobile ? "hidden md:table-cell" : ""
            } ${column.truncate || column.maxWidth ? "truncate" : ""}`}
            style={Object.keys(style).length > 0 ? style : undefined}
          >
            {getCellValue(column)}
          </td>
        );
      })}
    </tr>
  );
};

export default TicketLogRow;
